import React, { useState, useEffect } from 'react';
import './SitePermissionsModal.css';
import { api, useLocalStorageFallback } from '../services/api';

function SitePermissionsModal({ user, sites, onClose, onSave }) {
  const [enabledSites, setEnabledSites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const useFallback = useLocalStorageFallback();

  useEffect(() => {
    loadPermissions();
  }, [user]);

  const loadPermissions = async () => {
    setIsLoading(true);
    const userId = user.id || user.userId;
    
    if (useFallback) {
      // Load from localStorage
      const permissions = localStorage.getItem('zenio_user_site_permissions');
      if (permissions) {
        const permissionsObj = JSON.parse(permissions);
        setEnabledSites(permissionsObj[userId] || []);
      }
    } else {
      // Load from API
      try {
        const result = await api.getUserSitePermissions(userId);
        if (result.success) {
          setEnabledSites(result.enabledSites || []);
        }
      } catch (error) {
        console.error('Failed to load site permissions:', error);
      }
    }
    
    setIsLoading(false);
  };

  const handleToggleSite = (siteId) => {
    setEnabledSites(prev => {
      if (prev.includes(siteId)) {
        return prev.filter(id => id !== siteId);
      } else {
        return [...prev, siteId];
      }
    });
  };

  const handleSelectAll = () => {
    const allSiteIds = sites.map(site => site.id || site.siteId);
    setEnabledSites(allSiteIds);
  };

  const handleDeselectAll = () => {
    setEnabledSites([]);
  };

  const handleSave = async () => {
    const userId = user.id || user.userId;
    
    if (useFallback) {
      // Save to localStorage
      const permissions = localStorage.getItem('zenio_user_site_permissions');
      const permissionsObj = permissions ? JSON.parse(permissions) : {};
      permissionsObj[userId] = enabledSites;
      localStorage.setItem('zenio_user_site_permissions', JSON.stringify(permissionsObj));
    } else {
      // Save to API
      try {
        await api.updateUserSitePermissions(userId, enabledSites);
      } catch (error) {
        console.error('Failed to save site permissions:', error);
        alert('Failed to save site permissions. Please try again.');
        return;
      }
    }
    
    onSave(user, enabledSites);
    onClose();
  };

  return (
    <div className="site-permissions-overlay" onClick={onClose}>
      <div className="site-permissions-content" onClick={(e) => e.stopPropagation()}>
        <div className="site-permissions-header">
          <h3>Manage Sites for {user.name}</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="site-permissions-body">
          <div className="permissions-actions">
            <button onClick={handleSelectAll} className="select-all-btn">
              Select All
            </button>
            <button onClick={handleDeselectAll} className="deselect-all-btn">
              Deselect All
            </button>
            <span className="selected-count">
              {enabledSites.length} of {sites.length} sites enabled
            </span>
          </div>

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              Loading...
            </div>
          ) : (
            <div className="sites-checklist">
              {sites.map(site => {
                const siteId = site.id || site.siteId;
                const isEnabled = enabledSites.includes(siteId);
                
                return (
                  <div key={siteId} className="site-checkbox-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={() => handleToggleSite(siteId)}
                      />
                      <span className="site-name">{site.name}</span>
                      <span className="site-url">{site.url}</span>
                    </label>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="site-permissions-footer">
          <button onClick={onClose} className="cancel-button">
            Cancel
          </button>
          <button onClick={handleSave} className="save-button">
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

export default SitePermissionsModal;